import {
  InventoryCondition,
  InventoryEdition,
  Language,
} from './inventory-values';

export interface CardArtwork {
  id: string;
  imageUrl: string;
  isPrimary: boolean;
}

export interface AdminCardArtwork extends CardArtwork {
  provider: string;
  providerArtworkId: string;
  locale: string | null;
  isEnabled: boolean;
  sortOrder: number;
  primarySource: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCardPrintingArtwork {
  id: string;
  code: string;
  is_alternate_art?: boolean;
  isAlternateArt?: boolean;
  sort_order?: number;
  sortOrder?: number;
  collection: { id: string; name: string; code: string; gameId: string };
  rarity: CardPrinting['rarity'];
  artwork: CardArtwork | null;
}

export interface AdminCardArtworks {
  items: AdminCardArtwork[];
  printings: AdminCardPrintingArtwork[];
}

export interface ArtworkManifestImportResult {
  cardsInFile: number;
  matchedCards: number;
  unmatchedCards: number;
  artworksCreated: number;
  artworksUpdated: number;
  konamiReferencesUpdated: number;
  printingMappingsUpdated: number;
  ignoredPrintingMappings: number;
  unmatchedProviderIds: string[];
}

export interface Game {
  id: string;
  name: string;
  slug: string;
}

export interface Rarity {
  id: string;
  name: string;
  shortName?: string | null;
  code: string;
}

export interface Collection {
  id: string;
  gameId: string;
  name: string;
  code: string;
  preReleaseDate?: string | null;
  releaseDate?: string | null;
  cardCount?: number;
}

export interface CollectionInput {
  gameId: string;
  name: string;
  code: string;
  preReleaseDate?: string | null;
  releaseDate?: string | null;
}

export interface CatalogFilterTaxonomy {
  collections: Collection[];
  rarities: Rarity[];
}

export interface CardPrinting {
  id: string;
  code: string;
  is_alternate_art?: boolean;
  isAlternateArt?: boolean;
  sort_order?: number;
  sortOrder?: number;
  collection: { id: string; name: string; code: string; gameId: string };
  collectionId?: string;
  rarity: Rarity;
  artwork: CardArtwork | null;
  card?: SimplifiedCard | null;
}

export interface SimplifiedCard {
  id: string;
  slug: string;
  namePt?: string;
  nameEn: string;
  preReleaseUntil?: string;
  game: Game;
  primaryArtwork: CardArtwork | null;
}

/** @deprecated Use SimplifiedCard for compact card projections. */
export type CardSummary = SimplifiedCard;

export interface CatalogPreview {
  id: string;
  slug: string;
  namePt?: string;
  nameEn: string;
  imageUrl: string | null;
}

export interface CatalogSynchronization {
  id: string;
  provider: string;
  status: 'running' | 'succeeded' | 'failed';
  sourceCards: number | null;
  sourcePrintings: number | null;
  activePrintings: number | null;
  insertedPrintings: number | null;
  updatedPrintings: number | null;
  deactivatedPrintings: number | null;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
}

export const CATALOG_SORT = {
  NAME_ASC: 'name-asc',
  NAME_DESC: 'name-desc',
  PRINTING_CODE_ASC: 'printing-code-asc',
  PRICE_ASC: 'price-asc',
  PRICE_DESC: 'price-desc',
  QUANTITY_DESC: 'quantity-desc',
  OFFERS_DESC: 'offers-desc',
} as const;

export type CatalogSort = (typeof CATALOG_SORT)[keyof typeof CATALOG_SORT];

export interface CardSelection extends SimplifiedCard {
  printings: CardPrinting[];
}

export const YUGIOH_CARD_CATEGORY = {
  MONSTER: 'monster',
  SPELL: 'spell',
  TRAP: 'trap',
  SKILL: 'skill',
  OTHER: 'other',
} as const;

export type YuGiOhCardCategory =
  (typeof YUGIOH_CARD_CATEGORY)[keyof typeof YUGIOH_CARD_CATEGORY];

export const YUGIOH_CARD_CATEGORY_LABELS: Record<YuGiOhCardCategory, string> = {
  monster: 'Monstro',
  spell: 'Magia',
  trap: 'Armadilha',
  skill: 'Habilidade',
  other: 'Outro',
};

export interface YuGiOhCardDetails {
  kind: 'yugioh';
  frameType: string;
  category: YuGiOhCardCategory;
  attack?: number;
  defense?: number;
  race?: string;
  attribute?: string;
  typeLine: string[];
  level?: number;
  archetype?: string;
}

export type CardGameDetails = YuGiOhCardDetails;

export interface Card extends CardSelection {
  descriptionPt?: string;
  descriptionEn?: string;
  gameDetails?: CardGameDetails;
}

export const getCardDisplayName = (
  card: Pick<SimplifiedCard, 'namePt' | 'nameEn'>,
) => card.namePt || card.nameEn;

export const getCardDisplayDescription = (
  card: Pick<Card, 'descriptionPt' | 'descriptionEn'>,
) => card.descriptionPt || card.descriptionEn;

export interface CatalogCard extends SimplifiedCard {
  printingId?: string;
  displayArtwork?: CardArtwork | null;
  availableQuantity: number;
  offeredEditionCount?: number;
  offerCount: number;
  minimumPrice: number | null;
  originalMinimumPrice?: number;
  promotionEndsAt?: string;
  primaryArtwork: CardArtwork | null;
  displayRarity?: CardPrinting['rarity'] | null;
  displayCollection?: CardPrinting['collection'] | null;
  collections?: CardPrinting['collection'][];
  printings?: CardPrinting[];
}

export interface CatalogPage {
  items: CatalogCard[];
  nextCursor?: string;
  totalAvailableQuantity: number;
  totalCards: number;
  appliedFilters: Omit<CatalogFilters, 'cursor' | 'limit'>;
}

export interface CollectionCatalogCard extends CardSelection {
  availableQuantity: number;
  offerCount: number;
  minimumPrice: number | null;
  originalMinimumPrice?: number;
  promotionEndsAt?: string;
  averagePrice: number | null;
}

export interface CollectionCatalogPage {
  items: CollectionCatalogCard[];
  nextCursor?: string;
}

export interface CardDetails {
  card: Card & {
    availableQuantity: number;
    offerCount: number;
    minimumPrice: number | null;
    originalMinimumPrice?: number;
    promotionEndsAt?: string;
    primaryArtwork: CardArtwork | null;
    artworks?: CardArtwork[];
  };
  offers: CardOffer[];
  minimumPrice: number | null;
  originalMinimumPrice?: number;
  promotionEndsAt?: string;
  averagePrice: number | null;
  recentSales: {
    lastSoldPrice?: number | null;
    averageUnitPrice: number | null;
    unitSales: number;
  };
}

export interface LandingCatalogCard {
  id: string;
  slug: string;
  namePt?: string;
  nameEn: string;
  primaryArtwork: CardArtwork | null;
  latestOfferCreatedAt: string;
  availableQuantity: number;
  offerCount: number;
  minimumPrice: string;
}

export interface LandingCatalogSection {
  game: Game;
  cards: LandingCatalogCard[];
}

export interface CardOffer {
  id: string;
  store: {
    id: string;
    name: string;
    slug: string;
    rank?: { code: import('./store').StoreRank; label: string };
    address?: import('./store').PublicStoreAddress;
    shippingMethods: import('./store').StoreShippingMethod[];
  };
  printing: CardPrinting;
  language: Language;
  condition: InventoryCondition;
  edition: InventoryEdition | null;
  observation: string | null;
  quantity: number;
  price: string;
  originalPrice?: string;
  promotionEndsAt?: string;
}

export interface CatalogFilters {
  cardId?: string;
  search?: string;
  gameCode?: string;
  collectionId?: string;
  rarityId?: string;
  condition?: InventoryCondition;
  minPrice?: string;
  maxPrice?: string;
  stateId?: string;
  cityId?: string;
  limit?: number;
  status?: 'all' | 'active' | 'paused';
  sort?: CatalogSort;
  unlinkedCollectionsOnly?: boolean;
}

export interface CreateCardInput {
  nameEn: string;
  namePt?: string;
  descriptionEn?: string;
  descriptionPt?: string;
  gameCode?: string;
  collectionId?: string;
  rarityId?: string;
  printingCode?: string;
  file?: File;
  yugiohDetails?: {
    frameType: string;
    category?: YuGiOhCardCategory;
    attack?: number;
    defense?: number;
    level?: number;
    race?: string;
    attribute?: string;
    typeLine?: string[];
    archetype?: string;
  };
}

export interface AdminCardDetails {
  id: string;
  gameCode: string;
  namePt?: string | null;
  nameEn: string;
  descriptionPt?: string | null;
  descriptionEn?: string | null;
  yugiohDetails?: {
    frameType: string;
    category: YuGiOhCardCategory;
    attack: number | null;
    defense: number | null;
    level: number | null;
    race: string | null;
    attribute: string | null;
    typeLine: string[];
    archetype: string | null;
  } | null;
}

export interface UpdateCardInput {
  nameEn: string;
  namePt: string | null;
  descriptionEn: string | null;
  descriptionPt: string | null;
  yugiohDetails?: {
    frameType: string;
    category: YuGiOhCardCategory;
    attack: number | null;
    defense: number | null;
    level: number | null;
    race: string | null;
    attribute: string | null;
    typeLine: string[];
    archetype: string | null;
  };
}

export interface AdminCollectionDetails extends Collection {
  game: Game;
  numOfCards: number | null;
  providerId: string | null;
  marketplaceVisible: boolean;
  printings: AdminCollectionPrinting[];
}

export interface AdminCollectionPrinting extends AdminCardPrintingArtwork {
  active: boolean;
  card: { id: string; nameEn: string; namePt: string | null };
}
